import type { AnalyticsProperties } from './analytics';
import { readCookieConsent } from './cookieConsent';

export interface SignupIdentity {
  userId: string;
  email?: string;
}

interface SignupDispatch {
  trackProduct: (properties: AnalyticsProperties) => void;
  trackMeta: (event: 'StartTrial' | 'CompleteRegistration', eventId: string, properties: AnalyticsProperties) => void;
  trackGoogle: (payload: Record<string, unknown>) => void;
}

interface PendingSignup {
  identity: SignupIdentity;
  properties: AnalyticsProperties;
  dispatch: SignupDispatch;
  product: boolean;
  marketing: boolean;
  hashing: boolean;
}

const pending = new Map<string, PendingSignup>();
const sent = new Set<string>();
const marker = (userId: string, channel: string) => `caloriescience_signup_conversion:${userId}:${channel}`;

const wasSent = (key: string): boolean => {
  if (sent.has(key)) return true;
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
};

const sendOnce = (key: string, send: () => void) => {
  if (wasSent(key)) return;
  // Claim before dispatch, since callbacks may synchronously flush again.
  sent.add(key);
  try {
    send();
  } catch {
    // Optional vendor code must not turn successful account creation into an
    // authentication error or prevent the other channels from being dispatched.
    console.warn('[analytics] Signup conversion dispatch failed');
  }
  try { window.localStorage.setItem(key, '1'); } catch { /* Memory still prevents repeated callbacks. */ }
};

const hashEmail = async (email: string): Promise<{ sha256_email_address: string }> => {
  const normalized = email.trim().toLowerCase();
  const [local, domain] = normalized.split('@');
  const value = domain === 'gmail.com' || domain === 'googlemail.com'
    ? `${local.replace(/\./g, '')}@${domain}` : normalized;
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return { sha256_email_address: Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('') };
};

const sendGoogle = async (signup: PendingSignup) => {
  const { userId, email } = signup.identity;
  signup.hashing = true;
  try {
    const userData = email ? await hashEmail(email) : undefined;
    if (pending.get(userId) !== signup || !signup.marketing || !readCookieConsent()?.marketing) return;
    sendOnce(marker(userId, 'google'), () => signup.dispatch.trackGoogle({
      ...signup.properties,
      event: 'cs_signup_completed',
      event_id: `signup:${userId}`,
      transaction_id: `signup:${userId}`,
      ...(userData ? { user_data: userData } : {}),
    }));
    signup.identity.email = undefined;
    if (!signup.product) pending.delete(userId);
  } catch (error) {
    console.warn('[analytics] Signup conversion could not be prepared', error);
  } finally {
    signup.hashing = false;
  }
};

export const clearPendingSignupConversions = () => pending.clear();

export const flushPendingSignupConversions = () => {
  const consent = readCookieConsent();
  if (!consent) return;
  for (const [userId, signup] of pending) {
    signup.product = signup.product && consent.analytics;
    signup.marketing = signup.marketing && consent.marketing;
    if (signup.product) {
      sendOnce(marker(userId, 'product'), () => signup.dispatch.trackProduct(signup.properties));
      signup.product = false;
    }
    if (signup.marketing) {
      for (const event of ['StartTrial', 'CompleteRegistration'] as const) {
        sendOnce(marker(userId, event), () => signup.dispatch.trackMeta(event, `signup:${userId}:${event}`, signup.properties));
      }
      if (wasSent(marker(userId, 'google'))) pending.delete(userId);
      else if (!signup.hashing) void sendGoogle(signup);
    } else {
      pending.delete(userId);
    }
  }
};

export const queueSignupConversion = (
  identity: SignupIdentity,
  properties: AnalyticsProperties,
  dispatch: SignupDispatch,
) => {
  if (typeof window === 'undefined' || !identity.userId) return;
  const consent = readCookieConsent();
  if (consent && !consent.analytics && !consent.marketing) return;
  if (!pending.has(identity.userId)) {
    pending.set(identity.userId, {
      identity: { ...identity }, properties, dispatch,
      product: consent ? consent.analytics : true,
      marketing: consent ? consent.marketing : true,
      hashing: false,
    });
  }
  flushPendingSignupConversions();
};
